/**
 * Unified ContentService — domain consumer helpers (non-gallery namespaces).
 *
 * These are the public "front door" for each feature domain. They wrap
 * `fetchContent`/`getContentUrl` (the resolver + adapter chain) so UI code
 * never again hand-rolls env toggles, CDN-vs-disk branching, or signed-URL
 * logic. Each helper mirrors the legacy function it replaces:
 *
 *   hospitalityMenuUrls.hospitalityMenuImageUrl -> resolveHospitalityImageUrl
 *   curriculumSummaryAssetService              -> resolveCurriculumAssetUrl
 *   fsi s3KeyToFileUrl                      -> resolveFsiUrl
 *   radioStreamProxy usage                   -> resolveRadioStreamUrl
 *   photoAlbumService sign                   -> resolvePhotoAlbumUrl
 *   studio cover pathing                    -> resolveStudioUrl
 *   knowledge-base static path              -> resolveKbUrl
 *
 * Key conventions (must match src/content/namespaceConfig.js):
 *   hospitality  logicalKey = `hospitality-menu/{root}/{rel}`
 *   curriculum  logicalKey = bucket-relative key (`library/...`, `curriculum/...`)
 *   fsi         logicalKey = `fsi/{language}/{rest}`
 *   radio       logicalKey = upstream stream URL (passed through proxy)
 *   photoAlbum  logicalKey = bucket-relative key
 *   studio      logicalKey = `studio/{rest}`
 *   kb          logicalKey = knowledge-base rest (`math/index.json`)
 */

import { fetchContent, getContentUrl } from '../index.js';

function enc(s) {
  return String(s == null ? '' : s)
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
}

/* ------------------------------------------------------------------ */
/* HOSPITALITY                                                        */
/* ------------------------------------------------------------------ */

export function hospitalityRef(root, subDir, file) {
  const rel = [subDir, file].filter(Boolean).map(String).join('/').replace(/^\/+/, '');
  return {
    namespace: 'hospitality',
    logicalKey: `hospitality-menu/${root}/${rel}`,
    contentType: 'image',
    variant: 'full',
    requireSigned: false,
  };
}

export async function resolveHospitalityImageUrl(root, subDir, file, opts = {}) {
  return fetchContent(hospitalityRef(root, subDir, file), opts);
}

export function getHospitalityImageUrl(root, subDir, file) {
  return getContentUrl(hospitalityRef(root, subDir, file));
}

/* ------------------------------------------------------------------ */
/* CURRICULUM                                                          */
/* ------------------------------------------------------------------ */

export function curriculumRef(s3Key, { variant = 'full', contentType } = {}) {
  const key = String(s3Key || '').replace(/^\/+/, '');
  const isJson = /\.json$/i.test(key);
  return {
    namespace: 'curriculum',
    logicalKey: key,
    contentType: contentType || (isJson ? 'json' : 'file'),
    variant,
    requireSigned: !isJson,
  };
}

export async function resolveCurriculumAssetUrl(s3Key, opts = {}) {
  return fetchContent(curriculumRef(s3Key, opts), opts);
}

export function getCurriculumAssetUrl(s3Key, opts = {}) {
  return getContentUrl(curriculumRef(s3Key, opts));
}

/* ------------------------------------------------------------------ */
/* FSI (Foreign Service Institute audio/drill media)                    */
/* ------------------------------------------------------------------ */

export function fsiRef(language, rest) {
  const p = String(rest || '').replace(/^\/+/, '');
  return {
    namespace: 'fsi',
    logicalKey: `fsi/${String(language || '')}/${p}`,
    contentType: 'audio',
    variant: 'full',
    requireSigned: true,
  };
}

export async function resolveFsiUrl(language, rest, opts = {}) {
  return fetchContent(fsiRef(language, rest), opts);
}

export function getFsiUrl(language, rest) {
  return getContentUrl(fsiRef(language, rest));
}

/* ------------------------------------------------------------------ */
/* RADIO (HLS upstream stream proxy)                                   */
/* ------------------------------------------------------------------ */

export function radioRef(upstreamUrl) {
  return {
    namespace: 'radio',
    logicalKey: String(upstreamUrl || ''),
    contentType: 'stream',
    variant: 'full',
    requireSigned: false,
  };
}

export async function resolveRadioStreamUrl(upstreamUrl, opts = {}) {
  return fetchContent(radioRef(upstreamUrl), opts);
}

export function getRadioStreamUrl(upstreamUrl) {
  return getContentUrl(radioRef(upstreamUrl));
}

/* ------------------------------------------------------------------ */
/* PHOTO ALBUM (signed CloudFront media)                              */
/* ------------------------------------------------------------------ */

export function photoAlbumRef(s3Key, { variant = 'full' } = {}) {
  return {
    namespace: 'photoAlbum',
    logicalKey: String(s3Key || '').replace(/^\/+/, ''),
    contentType: variant === 'thumb' ? 'image' : 'file',
    variant,
    requireSigned: true,
  };
}

export async function resolvePhotoAlbumUrl(s3Key, opts = {}) {
  return fetchContent(photoAlbumRef(s3Key, opts), opts);
}

export function getPhotoAlbumUrl(s3Key, opts = {}) {
  return getContentUrl(photoAlbumRef(s3Key, opts));
}

/* ------------------------------------------------------------------ */
/* STUDIO (theater / collection / playlist covers — baked /data)       */
/* ------------------------------------------------------------------ */

export function studioRef(rest) {
  return {
    namespace: 'studio',
    logicalKey: `studio/${String(rest || '').replace(/^\/+/, '')}`,
    contentType: /\.(mp4|webm|mov)$/i.test(rest || '') ? 'video' : 'image',
    variant: 'full',
    requireSigned: false,
  };
}

export async function resolveStudioUrl(rest, opts = {}) {
  return fetchContent(studioRef(rest), opts);
}

export function getStudioUrl(rest) {
  return getContentUrl(studioRef(rest));
}

/* ------------------------------------------------------------------ */
/* KNOWLEDGE BASE (static JSON / pages)                               */
/* ------------------------------------------------------------------ */

export function kbRef(rest) {
  const p = String(rest || '').replace(/^\/+/, '');
  return {
    namespace: 'kb',
    logicalKey: p,
    contentType: /\.json$/i.test(p) ? 'json' : 'page',
    variant: 'full',
    requireSigned: false,
  };
}

export async function resolveKbUrl(rest, opts = {}) {
  return fetchContent(kbRef(rest), opts);
}

export function getKbUrl(rest) {
  return getContentUrl(kbRef(rest));
}

/* ------------------------------------------------------------------ */
/* Generic list resolver (manifest -> resolved URLs)                    */
/* ------------------------------------------------------------------ */

/**
 * Resolve a list of items into URLs via a ref-builder.
 * @param {Array} items
 * @param {(it:any, i:number) => object} refBuilder  returns a ContentRef
 * @param {object} [opts] forwarded to fetchContent
 */
export async function resolveList(items, refBuilder, opts = {}) {
  return Promise.all(
    items.map(async (it, i) => {
      const ref = refBuilder(it, i);
      try {
        const h = await fetchContent(ref, opts);
        return { ...(typeof it === 'object' && it ? it : {}), url: h.url, source: h.source };
      } catch {
        return { ...(typeof it === 'object' && it ? it : {}), url: null };
      }
    }),
  );
}
